import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Truck } from 'lucide-react';
import apiClient from '../../api/client';
import { useToast } from '../ui/Toast';
import { Button } from '../ui/Button';
import { cn } from '../../utils/cn';

export interface SupplierOption {
  id: string;
  code: string;
  name: string;
  apiEnabled: boolean;
}

export interface PurchaseDraft {
  supplierId: string;
  documentNumber: string;
  documentDate: string;
}

const norm = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();

/**
 * The supplier whose name the invoice prints: "AUTODOC SE" finds AUTODOC by name or code, a
 * first-word match being enough (legal suffixes differ between the paper and our record).
 */
export function matchSupplier(
  printed: string | null,
  list: SupplierOption[]
): SupplierOption | null {
  if (!printed) return null;
  const p = norm(printed);
  const first = p.split(' ')[0];
  return (
    list.find((s) => norm(s.name) === p || norm(s.code) === p) ??
    list.find(
      (s) => first.length >= 3 && (norm(s.name).split(' ')[0] === first || norm(s.code) === first)
    ) ??
    null
  );
}

/**
 * Purchases → "Import facture d'achat": who the goods are from and which invoice they came on.
 * The supplier is required — the receipt is recorded as that supplier's purchase. When the name on
 * the invoice matches no supplier, one click creates it (no API; the full sheet stays in admin).
 */
export function PurchaseBar({
  suppliers,
  printedSupplier,
  value,
  disabled,
  onChange,
  onCommitField,
  onSupplierCreated,
}: {
  suppliers: SupplierOption[];
  printedSupplier: string | null;
  value: PurchaseDraft;
  disabled: boolean;
  onChange: (next: PurchaseDraft) => void;
  /** A field is final (select changed, input blurred) — re-check the invoice against the register. */
  onCommitField: (next: PurchaseDraft) => void;
  onSupplierCreated: (s: SupplierOption) => void;
}) {
  const { t } = useTranslation();
  const { showApiError } = useToast();
  const [creating, setCreating] = useState(false);
  const canOfferCreate =
    !!printedSupplier && !value.supplierId && !matchSupplier(printedSupplier, suppliers);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await apiClient.post<SupplierOption>(
        '/purchasing/stock-import/suppliers',
        { name: printedSupplier },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      onSupplierCreated(data);
      const next = { ...value, supplierId: data.id };
      onChange(next);
      onCommitField(next);
    } catch (err) {
      showApiError(err);
    } finally {
      setCreating(false);
    }
  };

  const field = 'h-8 rounded-md border bg-background px-2 text-sm';
  return (
    <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border bg-muted/30 px-3 py-2.5">
      <Truck className="mb-1.5 h-5 w-5 shrink-0 text-primary" />
      <label className="text-xs text-muted-foreground">
        <span className="mb-1 block font-medium">{t('import.purchase.supplier')} *</span>
        <select
          className={cn(field, 'min-w-[14rem]', !value.supplierId && 'border-warning')}
          value={value.supplierId}
          disabled={disabled}
          onChange={(e) => {
            const next = { ...value, supplierId: e.target.value };
            onChange(next);
            onCommitField(next);
          }}
        >
          <option value="">{t('import.purchase.chooseSupplier')}</option>
          {suppliers.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name} ({s.code}){s.apiEnabled ? ' · API' : ''}
            </option>
          ))}
        </select>
      </label>
      {canOfferCreate && (
        <Button
          size="sm"
          variant="outline"
          disabled={disabled || creating}
          onClick={() => void create()}
        >
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          {t('import.purchase.createSupplier', { name: printedSupplier })}
        </Button>
      )}
      <label className="text-xs text-muted-foreground">
        <span className="mb-1 block font-medium">{t('import.purchase.invoiceNumber')}</span>
        <input
          className={cn(field, 'w-40')}
          value={value.documentNumber}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, documentNumber: e.target.value })}
          onBlur={() => onCommitField(value)}
        />
      </label>
      <label className="text-xs text-muted-foreground">
        <span className="mb-1 block font-medium">{t('import.purchase.invoiceDate')}</span>
        <input
          type="date"
          className={cn(field, 'w-40')}
          value={value.documentDate}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, documentDate: e.target.value })}
          onBlur={() => onCommitField(value)}
        />
      </label>
    </div>
  );
}
